import { useCartContext } from "app/providers/CartProvider";
import { useReducer } from "react";

export enum CustomerTypes {
  PERSONAL = "personal",
  BUSINESS = "business",
}

export interface PersonalInfoFormFields {
  type: CustomerTypes;
  name: string;
  surname: string;
  email: string;
  phoneNumber: string;

  invoiceReference: string;
  invoiceStreet: string;
  invoiceHouseNumber: string;
  invoiceCity: string;
  invoiceZipCode: string;
  invoiceMunicipality: string;
  invoiceProvince: string;

  street: string;
  houseNumber: string;
  city: string;
  zipCode: string;
  municipality: string;
  province: string;
  shippingPhone: string;

  isSameAddress: boolean;

  companyReference: string;
  companyName: string;
  companyPhoneNumber: string;
  btwNumber: string;
  vatNumber: string;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;

  agreedOnTerms: boolean;
  receiveLetters: boolean;
  isSubmitting: boolean;
}

export enum PersonalInfoFormActionType {
  UPDATE_TYPE,
  UPDATE_NAME,
  UPDATE_SURNAME,
  UPDATE_EMAIL,
  UPDATE_PHONE_NUMBER,

  UPDATE_INVOICE_REFERENCE,
  UPDATE_INVOICE_STREET,
  UPDATE_INVOICE_HOUSE_NUMBER,
  UPDATE_INVOICE_CITY,
  UPDATE_INVOICE_ZIP_CODE,
  UPDATE_INVOICE_MUNICIPALITY,
  UPDATE_INVOICE_PROVINCE,

  UPDATE_STREET,
  UPDATE_HOUSE_NUMBER,
  UPDATE_CITY,
  UPDATE_ZIP_CODE,
  UPDATE_MUNICIPALITY,
  UPDATE_PROVINCE,
  UPDATE_SHIPPING_PHONE,

  UPDATE_IS_SAME_ADDRESS,

  UPDATE_COMPANY_REFERENCE,
  UPDATE_COMPANY_NAME,
  UPDATE_CONTACT_FIRST_NAME,
  UPDATE_CONTACT_LAST_NAME,
  UPDATE_CONTACT_EMAIL,
  UPDATE_COMPANY_PHONE_NUMBER,
  UPDATE_BTW_NUMBER,
  UPDATE_VAT_NUMBER,

  UPDATE_AGREED_ON_TERMS,
  UPDATE_RECEIVE_LETTERS,
  UPDATE_IS_SUBMITTING,
}

export interface PersonalInfoFormAction {
  type: PersonalInfoFormActionType;
  payload: any;
}

const formReducer = (
  state: PersonalInfoFormFields,
  action: PersonalInfoFormAction,
) => {
  const { type, payload } = action;

  switch (type) {
    // personal info actions
    case PersonalInfoFormActionType.UPDATE_TYPE:
      return {
        ...state,
        type: payload,
      };
    case PersonalInfoFormActionType.UPDATE_NAME:
      return {
        ...state,
        name: payload,
      };
    case PersonalInfoFormActionType.UPDATE_SURNAME:
      return {
        ...state,
        surname: payload,
      };
    case PersonalInfoFormActionType.UPDATE_EMAIL:
      return {
        ...state,
        email: payload,
      };
    case PersonalInfoFormActionType.UPDATE_PHONE_NUMBER:
      return {
        ...state,
        phoneNumber: payload,
      };
    case PersonalInfoFormActionType.UPDATE_SHIPPING_PHONE:
      return {
        ...state,
        shippingPhone: payload,
      };

    //  invoice actions
    case PersonalInfoFormActionType.UPDATE_INVOICE_REFERENCE:
      return {
        ...state,
        invoiceReference: payload,
      };
    case PersonalInfoFormActionType.UPDATE_INVOICE_STREET:
      return {
        ...state,
        invoiceStreet: payload,
      };
    case PersonalInfoFormActionType.UPDATE_INVOICE_HOUSE_NUMBER:
      return {
        ...state,
        invoiceHouseNumber: payload,
      };
    case PersonalInfoFormActionType.UPDATE_INVOICE_CITY:
      return {
        ...state,
        invoiceCity: payload,
      };
    case PersonalInfoFormActionType.UPDATE_INVOICE_ZIP_CODE:
      return {
        ...state,
        invoiceZipCode: payload,
      };
    case PersonalInfoFormActionType.UPDATE_INVOICE_MUNICIPALITY:
      return {
        ...state,
        invoiceMunicipality: payload,
      };
    case PersonalInfoFormActionType.UPDATE_INVOICE_PROVINCE:
      return {
        ...state,
        invoiceProvince: payload,
      };

    //    delivery address actions
    case PersonalInfoFormActionType.UPDATE_STREET:
      return {
        ...state,
        street: payload,
      };
    case PersonalInfoFormActionType.UPDATE_HOUSE_NUMBER:
      return {
        ...state,
        houseNumber: payload,
      };
    case PersonalInfoFormActionType.UPDATE_CITY:
      return {
        ...state,
        city: payload,
      };
    case PersonalInfoFormActionType.UPDATE_ZIP_CODE:
      return {
        ...state,
        zipCode: payload,
      };
    case PersonalInfoFormActionType.UPDATE_MUNICIPALITY:
      return {
        ...state,
        municipality: payload,
      };
    case PersonalInfoFormActionType.UPDATE_PROVINCE:
      return {
        ...state,
        province: payload,
      };

    //    update is same address
    case PersonalInfoFormActionType.UPDATE_IS_SAME_ADDRESS:
      return {
        ...state,
        isSameAddress: payload,
      };

    //    company info
    case PersonalInfoFormActionType.UPDATE_COMPANY_REFERENCE:
      return {
        ...state,
        companyReference: payload,
      };
    case PersonalInfoFormActionType.UPDATE_COMPANY_NAME:
      return {
        ...state,
        companyName: payload,
      };
    case PersonalInfoFormActionType.UPDATE_COMPANY_PHONE_NUMBER:
      return {
        ...state,
        companyPhoneNumber: payload,
      };
    case PersonalInfoFormActionType.UPDATE_BTW_NUMBER:
      return {
        ...state,
        btwNumber: payload,
      };
    case PersonalInfoFormActionType.UPDATE_VAT_NUMBER:
      return {
        ...state,
        vatNumber: payload,
      };
    case PersonalInfoFormActionType.UPDATE_CONTACT_FIRST_NAME:
      return {
        ...state,
        contactFirstName: payload,
      };
    case PersonalInfoFormActionType.UPDATE_CONTACT_LAST_NAME:
      return {
        ...state,
        contactLastName: payload,
      };
    case PersonalInfoFormActionType.UPDATE_CONTACT_EMAIL:
      return {
        ...state,
        contactEmail: payload,
      };

    case PersonalInfoFormActionType.UPDATE_AGREED_ON_TERMS:
      return {
        ...state,
        agreedOnTerms: payload,
      };
    case PersonalInfoFormActionType.UPDATE_RECEIVE_LETTERS:
      return {
        ...state,
        receiveLetters: payload,
      };
    case PersonalInfoFormActionType.UPDATE_IS_SUBMITTING:
      return {
        ...state,
        isSubmitting: payload,
      };
  }
};

export const usePersonalInformationReducer = () => {
  const { isSameAddress, getCart, personalInfoType } = useCartContext();
  const cart = getCart();

  const initialState: PersonalInfoFormFields = {
    type: (personalInfoType as CustomerTypes) ?? CustomerTypes.PERSONAL,
    name: cart.address.shippingAddress.firstName,
    surname: cart.address.shippingAddress.lastName,
    email: cart.email,
    phoneNumber: cart.address.invoiceAddress.phone,
    shippingPhone: cart.address.shippingAddress.phone,
    invoiceReference: cart.address.invoiceAddress.reference,
    invoiceStreet: cart.address.invoiceAddress.street,
    invoiceHouseNumber: cart.address.invoiceAddress.houseNumber,
    invoiceCity: cart.address.invoiceAddress.city,
    invoiceZipCode: cart.address.invoiceAddress.zip,
    invoiceMunicipality: cart.address.invoiceAddress.municipality,
    invoiceProvince: cart.address.invoiceAddress.province,
    street: cart.address.shippingAddress.street,
    houseNumber: cart.address.shippingAddress.houseNumber,
    city: cart.address.shippingAddress.city,
    zipCode: cart.address.shippingAddress.zip,
    municipality: cart.address.shippingAddress.municipality,
    province: cart.address.shippingAddress.province,
    isSameAddress: isSameAddress,
    companyReference: cart.address.shippingAddress.reference,
    companyName: cart.address.shippingAddress.company,
    companyPhoneNumber: cart.address.invoiceAddress.phone,
    btwNumber: "", // this field is not yet implemented
    vatNumber: window.vo?.kvk ?? cart.address.shippingAddress.VAT,
    contactFirstName: cart.address.shippingAddress.firstName,
    contactLastName: cart.address.shippingAddress.lastName,
    contactEmail: cart.email,
    agreedOnTerms: false,
    receiveLetters: false,
    isSubmitting: false,
  };

  const [state, dispatch] = useReducer(formReducer, initialState);
  return { state, dispatch };
};
